/**
 * linkify.js — Auto-convert plain text URLs to clickable links in HTML content.
 *
 * Only converts URLs that are NOT already inside <a> tags.
 */

/**
 * URL regex pattern that matches common URL formats:
 * - https://example.com
 * - http://example.com
 * - www.example.com
 */
const URL_PATTERN = /\b(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+\.[^\s<>"']+)/gi;

/**
 * Convert plain text URLs in HTML to clickable anchor tags.
 * Skips URLs that are already inside <a> tags.
 *
 * @param {string} html - The HTML content to process
 * @returns {string} HTML with plain URLs converted to links
 */
export function linkifyHtml(html) {
  if (!html) return '';

  // Split HTML into segments: tags and text content
  // We only want to linkify text content, not URLs inside href attributes
  const result = [];
  let insideAnchor = false;
  let lastIndex = 0;

  // Match all HTML tags
  const tagPattern = /<\/?[a-z][^>]*>/gi;
  let match;

  while ((match = tagPattern.exec(html)) !== null) {
    const tag = match[0].toLowerCase();

    // Process text before this tag
    if (match.index > lastIndex) {
      const textSegment = html.slice(lastIndex, match.index);
      if (insideAnchor) {
        // Inside an anchor - don't linkify
        result.push(textSegment);
      } else {
        // Outside anchor - linkify URLs
        result.push(linkifyText(textSegment));
      }
    }

    // Add the tag itself
    result.push(match[0]);

    // Track if we're inside an <a> tag
    if (tag.startsWith('<a ') || tag === '<a>') {
      insideAnchor = true;
    } else if (tag === '</a>') {
      insideAnchor = false;
    }

    lastIndex = match.index + match[0].length;
  }

  // Process any remaining text after the last tag
  if (lastIndex < html.length) {
    const textSegment = html.slice(lastIndex);
    if (insideAnchor) {
      result.push(textSegment);
    } else {
      result.push(linkifyText(textSegment));
    }
  }

  return result.join('');
}

/**
 * Convert URLs in plain text to anchor tags.
 * @param {string} text - Plain text (not inside HTML tags)
 * @returns {string} Text with URLs converted to links
 */
function linkifyText(text) {
  return text.replace(URL_PATTERN, (url) => {
    // Ensure URL has a protocol
    // href for the raw URL (before trailing-punctuation cleanup)
    // Clean up any trailing punctuation that got captured
    const cleanUrl = url.replace(/[.,;:!?)]+$/, '');
    const cleanHref = cleanUrl.startsWith('http') ? cleanUrl : `https://${cleanUrl}`;
    const trailingPunctuation = url.slice(cleanUrl.length);
    
    return `<a href="${cleanHref}" target="_blank" rel="noopener noreferrer">${cleanUrl}</a>${trailingPunctuation}`;
  });
}

export default linkifyHtml;
